import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CLIENT_ERROR,
  CALL_STATUS,
  GUEST_UI_STATE,
  VOICE_CONNECTION_STATE,
  type CallDoc,
  type CallStatus,
  type GuestUiState,
  type VoiceConnectionState,
} from '@hotel-voice/shared';
import { GuestVoiceService } from '../services/agora/GuestVoiceService';
import {
  cancelCall,
  createCall,
  endCall,
  getAgoraCredentials,
  markCallConnected,
  markCallConnecting,
} from '../services/firebase/callables';
import { watchCall } from '../services/firebase/callWatcher';
import { toUserMessage } from '../utils/errors';
import type { GuestLocation } from '../utils/params';

export interface GuestCallState {
  uiState: GuestUiState;
  callStatus: CallStatus | null;
  connection: VoiceConnectionState;
  muted: boolean;
  /** Seconds since audio actually started, for the on-screen timer. */
  elapsedSeconds: number;
  errorMessage: string | null;
  startCall: () => Promise<void>;
  hangUp: () => Promise<void>;
  toggleMute: () => Promise<void>;
  reset: () => void;
}

/**
 * Orchestrates a guest call.
 *
 * The division of labour: Cloud Functions own the call's authoritative state,
 * Firestore streams it here, and Agora carries the audio. This hook never
 * writes a status — it reports events (`markCallConnecting` / `markCallConnected`)
 * and renders whatever the server says.
 */
export function useGuestCall(location: GuestLocation | null): GuestCallState {
  const [uiState, setUiState] = useState<GuestUiState>(GUEST_UI_STATE.IDLE);
  const [callStatus, setCallStatus] = useState<CallStatus | null>(null);
  const [connection, setConnection] = useState<VoiceConnectionState>(
    VOICE_CONNECTION_STATE.DISCONNECTED,
  );
  const [muted, setMuted] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const voiceRef = useRef<GuestVoiceService | null>(null);
  const callIdRef = useRef<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const connectedReportedRef = useRef(false);

  const voice = () => {
    voiceRef.current ??= new GuestVoiceService();
    return voiceRef.current;
  };

  /* ---------------------------------------------------------------------- */
  /* Teardown                                                               */
  /* ---------------------------------------------------------------------- */

  const teardown = useCallback(async () => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    connectedReportedRef.current = false;
    await voiceRef.current?.destroy().catch(() => undefined);
    voiceRef.current = null;
    setConnection(VOICE_CONNECTION_STATE.DISCONNECTED);
    setMuted(false);
  }, []);

  // Leaving the channel on unload matters: a guest who closes the tab mid-call
  // would otherwise keep the receptionist connected to silence until the
  // server-side sweeper notices.
  useEffect(() => {
    const handleUnload = () => {
      void voiceRef.current?.leave();
    };
    window.addEventListener('pagehide', handleUnload);
    return () => {
      window.removeEventListener('pagehide', handleUnload);
      void teardown();
    };
  }, [teardown]);

  /* ---------------------------------------------------------------------- */
  /* Call duration timer                                                    */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (uiState !== GUEST_UI_STATE.CONNECTED) return;

    // Measured from when the UI entered CONNECTED. `reset()` zeroes the
    // counter between calls, so the effect body stays free of setState.
    const startedAt = Date.now();
    const timer = window.setInterval(
      () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [uiState]);

  /* ---------------------------------------------------------------------- */
  /* Server-driven state                                                    */
  /* ---------------------------------------------------------------------- */

  const applyServerStatus = useCallback(
    (call: CallDoc | null) => {
      if (!call) return;
      setCallStatus(call.status);

      switch (call.status) {
        case CALL_STATUS.RINGING:
          setUiState(GUEST_UI_STATE.RINGING);
          break;
        case CALL_STATUS.ACCEPTED:
        case CALL_STATUS.CONNECTING:
        case CALL_STATUS.CONNECTED:
          setUiState(GUEST_UI_STATE.CONNECTED);
          break;
        case CALL_STATUS.DECLINED:
        case CALL_STATUS.MISSED:
        case CALL_STATUS.CANCELLED:
        case CALL_STATUS.ENDED:
          setUiState(GUEST_UI_STATE.ENDED);
          void teardown();
          break;
        case CALL_STATUS.FAILED:
          setErrorMessage("We couldn't connect to reception. Please try again.");
          setUiState(GUEST_UI_STATE.FAILED);
          void teardown();
          break;
        default:
          break;
      }
    },
    [teardown],
  );

  /* ---------------------------------------------------------------------- */
  /* Actions                                                                */
  /* ---------------------------------------------------------------------- */

  const startCall = useCallback(async () => {
    if (!location) return;

    setErrorMessage(null);
    setUiState(GUEST_UI_STATE.CALLING);

    try {
      const service = voice();

      await service.initialize({
        onConnectionStateChanged: (state) => {
          setConnection(state);
          if (state === VOICE_CONNECTION_STATE.RECONNECTING) {
            setUiState(GUEST_UI_STATE.RECONNECTING);
          }
          if (state === VOICE_CONNECTION_STATE.FAILED) {
            setErrorMessage(toUserMessage({ details: { code: CLIENT_ERROR.AGORA_JOIN_FAILED } }));
          }
        },
        onRemoteJoined: () => {
          // Audio is flowing. Tell the server, which stamps `connectedAt` —
          // the point call duration is measured from.
          setUiState(GUEST_UI_STATE.CONNECTED);
          if (!connectedReportedRef.current && callIdRef.current) {
            connectedReportedRef.current = true;
            void markCallConnected({
              hotelId: location.hotelId,
              callId: callIdRef.current,
            }).catch(() => undefined);
          }
        },
        onTokenWillExpire: () => {
          if (!callIdRef.current) return;
          void getAgoraCredentials({
            hotelId: location.hotelId,
            callId: callIdRef.current,
          })
            .then((credentials) => service.renewToken(credentials.token))
            .catch(() => undefined);
        },
        onError: (error) => setErrorMessage(toUserMessage(error)),
      });

      const call = await createCall({ hotelId: location.hotelId, roomId: location.roomId });
      callIdRef.current = call.callId;
      setCallStatus(call.status);
      setUiState(GUEST_UI_STATE.RINGING);

      unsubscribeRef.current = watchCall(location.hotelId, call.callId, applyServerStatus);

      // Join immediately rather than waiting for the accept: the channel is
      // ready the instant the receptionist answers, so there is no dead air.
      await service.join({
        appId: call.agora.appId,
        channelName: call.agora.channelName,
        token: call.agora.token,
        uid: call.agora.uid,
      });

      await markCallConnecting({
        hotelId: location.hotelId,
        callId: call.callId,
      }).catch(() => undefined);
    } catch (error) {
      setErrorMessage(toUserMessage(error));
      setUiState(GUEST_UI_STATE.FAILED);
      await teardown();

      // A call that was created but could not be joined must not keep ringing
      // a receptionist for a guest who is not there.
      if (callIdRef.current && location) {
        await cancelCall({ hotelId: location.hotelId, callId: callIdRef.current }).catch(
          () => undefined,
        );
        callIdRef.current = null;
      }
    }
  }, [applyServerStatus, location, teardown]);

  const hangUp = useCallback(async () => {
    if (!location || !callIdRef.current) return;

    const callId = callIdRef.current;
    const ringing =
      callStatus === CALL_STATUS.RINGING || callStatus === CALL_STATUS.INITIATED;

    await voiceRef.current?.leave().catch(() => undefined);

    try {
      // Cancelling and ending are different events: only the latter records a
      // duration, and the server rejects the wrong one for the current status.
      await (ringing
        ? cancelCall({ hotelId: location.hotelId, callId })
        : endCall({ hotelId: location.hotelId, callId }));
    } catch (error) {
      // The call may have ended underneath us — the listener will settle the UI.
      setErrorMessage(toUserMessage(error));
    }

    setUiState(GUEST_UI_STATE.ENDED);
    await teardown();
  }, [callStatus, location, teardown]);

  const toggleMute = useCallback(async () => {
    const service = voiceRef.current;
    if (!service) return;

    if (service.isMuted()) {
      await service.unmute();
      setMuted(false);
    } else {
      await service.mute();
      setMuted(true);
    }
  }, []);

  const reset = useCallback(() => {
    callIdRef.current = null;
    setCallStatus(null);
    setElapsedSeconds(0);
    setErrorMessage(null);
    setUiState(GUEST_UI_STATE.IDLE);
  }, []);

  return {
    uiState,
    callStatus,
    connection,
    muted,
    elapsedSeconds,
    errorMessage,
    startCall,
    hangUp,
    toggleMute,
    reset,
  };
}
